<template>
  <div class="onboarding-connect-natives">
    <!-- Left selection : steps -->
    <OnboardingSteps
      :current-step="'natives'"
      :current-network="currentNetwork"
      @network-selected="handleNetworkSelected"
    />

    <!-- Right section : native connection -->
    <div class="onboarding-content">
      <!-- Title section -->
      <div class="onboarding-header">
        <div class="onboarding-network-icon-wrapper">
          <img
            :src="currentNetworkIcon"
            :alt="currentNetworkName"
            class="onboarding-network-icon"
          />
          <!-- Current page avatar in bottom-right corner -->
          <img
            v-if="currentPage && currentPage.avatar && !imageLoadErrors[currentPage.id]"
            :src="currentPage.avatar"
            :alt="currentPageName"
            class="onboarding-current-page-avatar"
            @error="handleImageError(currentPage.id)"
          />
          <!-- Show placeholder if no avatar or image error -->
          <div
            v-else-if="currentPage"
            class="onboarding-current-page-avatar avatar-placeholder"
          >
            {{ currentPageName.charAt(0).toUpperCase() }}
          </div>
        </div>
        <div class="onboarding-header-content">
          <h1 class="onboarding-header-title">
            {{ $t('onboarding.connect_natives.connection_title', { networkName: currentNetworkName }) }}
            <svg class="native-icon" width="26" height="26" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" stroke-width="3" stroke="none" fill="#111827"><path d="M37.34,7.36a.12.12,0,0,1,.18.13c-.47,1.86-2.78,12.63,5.57,19.62,8.16,6.84,8.41,17.13,2.33,24-7.27,8.23-19.84,6.78-25.25,1.37C16.36,48.69,9.44,36.33,21.29,26a.1.1,0,0,1,.16,0c.29,1.23,2.3,9,7.66,10,.25,0,.37-.11.25-.34C27.78,32.6,20.66,17,37.34,7.36Z" stroke-linecap="round"/></svg>
          </h1>
          <p class="onboarding-header-subtitle" v-html="$t('onboarding.connect_natives.connection_subtitle', { pageName: currentPageName })">
          </p>
        </div>
      </div>

      <!-- Features badges that require native connection -->
      <div class="onboarding-features-badges" v-if="networkFeatures.length > 0">
        <span
          class="onboarding-feature-badge"
          v-for="feature in networkFeatures"
          :key="feature"
          :data-tooltip="$t(`onboarding.features.${feature}.description`)"
        >
          {{ $t(`onboarding.features.${feature}.title`) }}
        </span>
      </div>

      <!-- Native connection form (hidden after successful connection) -->
      <div v-if="!connectionSuccess" class="onboarding-native-form">
        <!-- Error message -->
        <div v-if="connectionError" class="onboarding-message onboarding-message-error">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M10 6V10M10 14H10.01M19 10C19 14.9706 14.9706 19 10 19C5.02944 19 1 14.9706 1 10C1 5.02944 5.02944 1 10 1C14.9706 1 19 5.02944 19 10Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span>{{ connectionError }}</span>
        </div>

        <div class="onboarding-form-group">
          <label class="onboarding-form-label">
            {{ $t('onboarding.connect_natives.form_label_username') }}
          </label>
          <input
            type="text"
            name="username"
            autocomplete="username"
            v-model="nativeCredentials.username"
            class="onboarding-form-input"
            :placeholder="currentPageName"
          />
        </div>

        <div class="onboarding-form-group">
          <label class="onboarding-form-label">
            {{ $t('onboarding.connect_natives.form_label_password') }}
          </label>
          <input
            type="password"
            name="password"
            autocomplete="current-password"
            v-model="nativeCredentials.password"
            class="onboarding-form-input"
            placeholder=""
          />
        </div>

        <button
          class="onboarding-btn-connect"
          @click="handleNativeConnection"
          :disabled="!canConnect || isConnecting"
        >
          <!-- Loading state -->
          <span v-if="isConnecting" class="onboarding-btn-loading">
            <svg class="onboarding-spinner" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path opacity="0.2" d="M10 1C5.02944 1 1 5.02944 1 10C1 14.9706 5.02944 19 10 19C14.9706 19 19 14.9706 19 10C19 5.02944 14.9706 1 10 1Z" stroke="currentColor" stroke-width="2"/>
              <path d="M10 1C14.9706 1 19 5.02944 19 10" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
            {{ $t('onboarding.connect_natives.button_connecting') }}
          </span>
          <span v-else>
            {{ $t('onboarding.connect_natives.button_connect') }}
          </span>
        </button>
      </div>

      <!-- Success section (shown after successful connection, before user clicks Continue) -->
      <div v-else class="onboarding-native-success">
        <div class="onboarding-native-success-icon">
          <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
            <circle cx="32" cy="32" r="32" fill="#10B981" fill-opacity="0.1"/>
            <path d="M20 32L28 40L44 24" stroke="#10B981" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <h3 class="onboarding-native-success-title">{{ $t('onboarding.connect_natives.success_title') }}</h3>
        <p class="onboarding-native-success-message">{{ $t('onboarding.connect_natives.success_message') }}</p>
      </div>

      <!-- Skip warning modal (inline, supports 3 buttons: Stay / Next page / Exit) -->
      <div class="onboarding-skip-modal-overlay" v-if="showSkipModal" @click.self="closeSkipModal">
        <div class="onboarding-skip-modal">
          <h4>{{ $t('onboarding.modale.native.exit.title') }}</h4>
          <div class="onboarding-skip-modal-body" v-html="skipModalContent"></div>
          <div class="onboarding-skip-modal-buttons">
            <div class="button btn-outline-primary" @click="closeSkipModal">
              {{ $t('onboarding.modale.exit.cancel') }}
            </div>
            <div
              v-if="nextPage"
              class="button btn-outline-primary"
              @click="confirmSkipCurrentPage"
            >
              {{ $t('onboarding.connect_natives.skip_page') }}
            </div>
            <div class="button btn-primary" @click="confirmSkip">
              {{ $t('onboarding.modale.exit.confirm') }}
            </div>
          </div>
        </div>
      </div>

      <!-- Two-Factor Authentication Modal -->
      <TwoFactorModal
        :active.sync="twoFactorActive"
        :page="refreshedCurrentPage"
        @connected="handleTwoFactorSuccess"
        @cancel="twoFactorActive = false"
      />

      <!-- Privacy reassurance mention -->
      <p class="onboarding-privacy-mention">
        <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style="flex-shrink: 0;">
          <path d="M10 2L3 5V10C3 14.418 6.03 18.528 10 19.5C13.97 18.528 17 14.418 17 10V5L10 2Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        {{ $t('onboarding.connect_natives.privacy_mention') }}
      </p>

      <!-- Footer: Skip (before success) or Continue (after success) -->
      <div class="onboarding-footer-container">
        <div class="onboarding-footer">
          <button
            v-if="!connectionSuccess"
            class="onboarding-btn-secondary"
            @click="showSkipWarning"
            :disabled="isConnecting"
          >
            {{ $t('onboarding.connect_natives.skip_step') }}
          </button>
          <button
            v-if="connectionSuccess"
            class="onboarding-btn-primary"
            @click="continueAfterSuccess"
          >
            {{ continueButtonLabel }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import Bots from '../../mixins/Bots.vue';
import Notif from '../../mixins/Notif.vue';
import Pages from '../../mixins/Pages.vue';
import OnboardingSteps from '../shared/OnboardingSteps.vue';
import { persistOnboardingPages } from '../utils/onboardingPersistence';
import { getNetworkName, getNetworkIcon } from '../utils/networkMaps';
import { lazyComponent } from '@/utils/appVersion';

export default {
  name: 'StepConnectNatives',

  components: {
    OnboardingSteps,
    TwoFactorModal: lazyComponent(() => import('../shared/TwoFactorModal.vue'))
  },

  mixins: [Bots, Notif, Pages],

  data() {
    return {
      currentNetworkIndex: 0,
      currentPageIndex: 0,
      nativeCredentials: {
        username: '',
        password: ''
      },
      isConnecting: false,
      connectionError: null,
      connectionSuccess: false,
      twoFactorActive: false,
      allPages: [],
      imageLoadErrors: {},
      showSkipModal: false,
      skipModalContent: ''
    }
  },

  computed: {
    ...mapState('onboarding', [
      'selectedNetworks',
      'selectedFeatures',
      'connectedPages',
      'selectedPageIds'
    ]),

    currentNetwork() {
      return this.selectedNetworks[this.currentNetworkIndex] || null
    },

    currentNetworkName() {
      return getNetworkName(this.currentNetwork)
    },

    currentNetworkIcon() {
      return getNetworkIcon(this.currentNetwork)
    },

    // Get pages that were selected in StepConnectPages from store
    // Exclude pages that already have a native connection (story_config.created = true)
    selectedPages() {
      const pageIds = this.selectedPageIds[this.currentNetwork] || []
      const allPages = this.connectedPages[this.currentNetwork] || []
      
      // Filter to only include selected pages that DON'T have a native connection yet
      return allPages.filter(page => {
        const isSelected = pageIds.includes(page.id)
        const hasNativeConnection = page.story_config && page.story_config.created === true
        
        // Only include if selected AND no native connection exists
        return isSelected && !hasNativeConnection
      })
    },

    currentPage() {
      return this.selectedPages[this.currentPageIndex] || null
    },

    currentPageId() {
      return this.currentPage?.id || null
    },

    currentPageName() {
      return this.currentPage?.name || ''
    },

    // Get the refreshed page with story_config from allPages after refreshPages() call
    refreshedCurrentPage() {
      if (!this.currentPage || !this.allPages || this.allPages.length === 0) {
        return this.currentPage
      }
      
      // Find the refreshed version of current page in allPages
      const refreshed = this.allPages.find(p => p.id === this.currentPage.id)
      
      if (refreshed) {
        console.log('[StepConnectNatives] Using refreshed page with story_config:', refreshed.story_config)
        return refreshed
      }
      
      console.warn('[StepConnectNatives] Refreshed page not found in allPages, using current page')
      return this.currentPage
    },

    totalPages() {
      return this.selectedPages.length
    },

    // Get the next page to connect (for button preview)
    nextPage() {
      if (this.currentPageIndex < this.selectedPages.length - 1) {
        return this.selectedPages[this.currentPageIndex + 1]
      }
      return null
    },

    // Get features that require native connection for this network
    networkFeatures() {
      if (!this.currentNetwork) return []
      
      // Check if user selected native connection for this network
      const hasNative = this.selectedFeatures.some(f => 
        f === `${this.currentNetwork}.native` || f === 'twitter.native' || f === 'x.native'
      )
      
      if (!hasNative) return []
      
      // Return all feature labels for this network to display as badges
      const featureLabels = {
        facebook: [
          'facebook.native_post',
          'facebook.groups',
          'facebook.personal',
        ],
        instagram: [
          'instagram.native_post',
          'instagram.stories',
          'instagram.mention',
          'instagram.music',
          'instagram.hashtag',
          'instagram.sticker'
        ],
        linkedin: [
          'linkedin.native_post',
          'linkedin.mentions',
          'linkedin.groups',
          'linkedin.carousel',
          'linkedin.poll',
          'linkedin.inbox'
        ],
        tiktok: [
          'tiktok.mentions',
          'tiktok.music',
          'tiktok.video',
          'tiktok.reporting',
          // 'tiktok.stories'
        ]
      }
      
      return featureLabels[this.currentNetwork] || []
    },

    // Check if current page has a native connection established
    currentPageHasNativeConnection() {
      if (!this.currentPage) return false
      
      // Check if story_config exists and created is true
      const hasNativeConnection = this.currentPage.story_config?.created === true
      
      console.log('[StepConnectNatives] currentPageHasNativeConnection:', hasNativeConnection)
      console.log('[StepConnectNatives] currentPage.story_config:', this.currentPage.story_config)
      
      return hasNativeConnection
    },

    canConnect() {
      return this.nativeCredentials.username.trim() !== '' &&
             this.nativeCredentials.password.trim() !== '' &&
             !this.isConnecting
    },

    // Determine if button should be disabled
    buttonDisabled() {
      // If not connected yet, disable if can't connect
      if (!this.currentPageHasNativeConnection) {
        return !this.canConnect
      }
      // If connected, never disable (always allow navigation)
      return false
    },

    // Label for the footer Continue button
    continueButtonLabel() {
      if (this.selectedPages.length > 0 && this.currentPage) {
        return this.$t('onboarding.connect_natives.next_connection')
      }
      return this.$t('onboarding.connect_natives.finish_network')
    }
  },

  watch: {
    currentPage: {
      immediate: true,
      handler(newPage) {
        if (newPage) {
          // Pre-fill username: prefer page.instagram_username for Instagram,
          // then page.username, fallback to page.name
          const username = (this.currentNetwork === 'instagram' ? newPage.instagram_username : newPage.username) || newPage.username || newPage.name
          if (username) {
            this.nativeCredentials.username = username
            console.log('[StepConnectNatives] Auto-filled username:', username)
          }
        }
      }
    },

    // Persist current network index so plain page reloads return to the right network
    currentNetworkIndex(val) {
      localStorage.setItem('onboarding_natives_network_index', val.toString())
    },

    // Deferred guard for plain page reloads.
    // Child mounts before parent (Onboarding.vue), so selectedNetworks is empty when mounted()
    // fires. This watcher triggers once the parent has restored Vuex from localStorage,
    // then re-runs the guard checks that were skipped.
    selectedNetworks: {
      handler(networks) {
        if (!this._deferredGuard || networks.length === 0) return
        this._deferredGuard = false
        console.log('[StepConnectNatives] selectedNetworks populated after reload - running deferred guard')

        // Restore network index (saved by currentNetworkIndex watcher above)
        const savedIndex = localStorage.getItem('onboarding_natives_network_index')
        if (savedIndex !== null) {
          const parsed = Number.parseInt(savedIndex)
          if (!Number.isNaN(parsed) && parsed > 0 && parsed < networks.length) {
            console.log('[StepConnectNatives] Restoring network index:', parsed, '→', networks[parsed])
            this.currentNetworkIndex = parsed
          }
        }

        if (!this.selectedPages || this.selectedPages.length === 0) {
          console.warn('[StepConnectNatives] No pages selected - redirecting to connectPages step')
          this.$router.push({ name: 'onboarding-connectPages' })
          return
        }
        if (!this.networkFeatures || this.networkFeatures.length === 0) {
          console.log('[StepConnectNatives] Current network has no native features, skipping')
          this.goToNextNetwork()
        }
      }
    }
  },

  methods: {
    

    // Handle button click - either connect or navigate based on connection status
    handleButtonClick() {
      if (this.currentPageHasNativeConnection) {
        this.continueAfterSuccess()
      } else {
        this.handleNativeConnection()
      }
    },

    async handleNativeConnection() {
      if (!this.canConnect || this.isConnecting) {
        console.log('[StepConnectNatives] Cannot connect - conditions not met')
        return
      }

      console.log('=== [StepConnectNatives] STARTING NATIVE CONNECTION ===')
      console.log('[StepConnectNatives] Current page:', this.currentPage)
      console.log('[StepConnectNatives] Page ID:', this.currentPageId)
      console.log('[StepConnectNatives] Page name:', this.currentPageName)
      console.log('[StepConnectNatives] Network:', this.currentNetwork)
      console.log('[StepConnectNatives] Credentials - username:', this.nativeCredentials.username)
      
      // Save state to localStorage in case of refresh during OAuth/2FA flow
      localStorage.setItem('onboarding_native_state', JSON.stringify({
        currentNetworkIndex: this.currentNetworkIndex,
        currentPageIndex: this.currentPageIndex
      }))
      console.log('[StepConnectNatives] State saved to localStorage')
      
      this.isConnecting = true
      this.connectionError = null
      this.connectionSuccess = false

      // IMPORTANT: Refresh page from backend to ensure all fields are up-to-date
      // The page from store might be missing fields that backend expects
      console.log('[StepConnectNatives] Refreshing page from backend before connection...')
      try {
        await this.refreshPages()
        const freshPage = this.allPages.find(p => p.id === this.currentPageId)
        if (freshPage) {
          console.log('[StepConnectNatives] Fresh page from backend:', freshPage)
          console.log('[StepConnectNatives] Fresh page network:', freshPage.network)
          console.log('[StepConnectNatives] Fresh page instagram_username:', freshPage.instagram_username)
        } else {
          console.warn('[StepConnectNatives] WARNING: Page not found in backend after refresh!')
        }
      } catch (error) {
        console.error('[StepConnectNatives] Failed to refresh pages:', error)
      }

      const isUuid = (str) => typeof str === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str)
      const validPageId = isUuid(this.currentPageId) ? this.currentPageId : 'createPage'
      const body = {
        pageId: validPageId,
        usernameAccount: this.nativeCredentials.username,
        passwordAccount: this.nativeCredentials.password
      }
      console.log('[StepConnectNatives] API request body:', {
        pageId: body.pageId,
        usernameAccount: body.usernameAccount,
        passwordLength: body.passwordAccount?.length || 0
      })

      try {
        console.log('[StepConnectNatives] Calling connectLinkedinDirect API...')
        const response = await this.connectLinkedinDirect(body)
        
        console.log('[StepConnectNatives] API response received:', {
          ok: response.ok,
          body: response.body
        })

        if (response.ok && response.body) {
          // Check if page ID changed from backend (common for Instagram checkpoint/creation)
          const newId = response.body.id
          if (newId && newId !== this.currentPageId) {
            console.log(`[StepConnectNatives] Page ID changed from backend: ${this.currentPageId} -> ${newId}`)
            this.$store.commit('onboarding/REPLACE_PAGE_ID', {
              network: this.currentNetwork,
              oldPageId: this.currentPageId,
              newPageId: newId
            })
          }

          if (response.body.connected) {
            // Connection successful
            console.log('[StepConnectNatives] Native connection successful')
            console.log('[StepConnectNatives] story_config:', response.body.story_config)
            
            // Save to store
            this.$store.commit('onboarding/SET_NATIVE_CONNECTION', {
              network: this.currentNetwork,
              pageId: this.currentPageId,
              connected: true,
              storyConfig: response.body.story_config
            })
            console.log('[StepConnectNatives] Saved to store - nativeConnections:', this.$store.state.onboarding.nativeConnections)

            // Update the page in store with story_config
            if (response.body.story_config) {
              // Find the page in store
              const pageInStore = this.connectedPages[this.currentNetwork]?.find(
                p => p.id === this.currentPageId
              )
              
              if (pageInStore) {
                // Create updated page object with story_config
                const updatedPage = {
                  ...pageInStore,
                  story_config: response.body.story_config
                }
                
                // Update in store via mutation
                this.$store.commit('onboarding/UPDATE_PAGE', {
                  network: this.currentNetwork,
                  pageId: this.currentPageId,
                  updates: { story_config: response.body.story_config }
                })
                
                console.log('[StepConnectNatives] Updated page story_config in store')
              }
            }

            this.connectionSuccess = true
            // Persist immediately so state survives any unexpected navigation
            this.persistOnboardingStateBeforeNavigation()
            
            this.notify({
              time: 5000,
              title: this.$t('onboarding.connect_natives.success_title'),
              text: this.$t('onboarding.connect_natives.success_message'),
              color: 'success'
            })

            // Refresh pages
            console.log('[StepConnectNatives] Refreshing pages from API...')
            await this.getPages()
            console.log('[StepConnectNatives] Pages refreshed')
            console.log('[StepConnectNatives] Connection complete - user can now click footer button to continue')

          } else if (response.body.twoAuth) {
            // Two-factor authentication required
            console.log('[StepConnectNatives] 2FA required - opening modal')
            console.log('[StepConnectNatives] Current page before refresh:', this.currentPage)
            
            // Refresh pages to get story_config with 2FA info
            await this.refreshPages()
            
            console.log('[StepConnectNatives] Pages refreshed, allPages length:', this.allPages.length)
            console.log('[StepConnectNatives] Refreshed current page:', this.refreshedCurrentPage)
            console.log('[StepConnectNatives] story_config:', this.refreshedCurrentPage?.story_config)
            
            // Open 2FA modal
            this.twoFactorActive = true
            console.log('[StepConnectNatives] twoFactorActive set to true')

          } else if (response.body.challengeInstagram) {
            // Challenge Instagram (also requires 2FA modal)
            console.log('[StepConnectNatives] Instagram challenge required - opening modal')
            console.log('[StepConnectNatives] Current page before refresh:', this.currentPage)
            
            // Refresh pages to get story_config with challenge info
            await this.refreshPages()
            
            console.log('[StepConnectNatives] Pages refreshed, allPages length:', this.allPages.length)
            console.log('[StepConnectNatives] Refreshed current page:', this.refreshedCurrentPage)
            console.log('[StepConnectNatives] story_config:', this.refreshedCurrentPage?.story_config)
            
            // Open 2FA modal
            this.twoFactorActive = true
            console.log('[StepConnectNatives] twoFactorActive set to true')

          } else if (response.body.awaitingApproval) {
            // Approve from another device - no code to enter, modal shows a waiting/polling state
            console.log('[StepConnectNatives] Awaiting approval from another device - opening modal')
            console.log('[StepConnectNatives] Current page before refresh:', this.currentPage)

            // Refresh pages to get story_config with the pending approval info
            await this.refreshPages()

            console.log('[StepConnectNatives] Pages refreshed, allPages length:', this.allPages.length)
            console.log('[StepConnectNatives] Refreshed current page:', this.refreshedCurrentPage)
            console.log('[StepConnectNatives] story_config:', this.refreshedCurrentPage?.story_config)

            // Open 2FA modal
            this.twoFactorActive = true
            console.log('[StepConnectNatives] twoFactorActive set to true')

          } else if (response.body.captcha) {
            console.log('[StepConnectNatives] CAPTCHA required')
            this.connectionError = this.$t('dash.bots.captcha')
            this.notify({
              time: 10000,
              title: this.$t('onboarding.connect_natives.error_connection_title'),
              text: this.$t('dash.bots.captcha'),
              color: 'danger'
            })

          } else if (response.body.badPassword) {
            console.log('[StepConnectNatives] Bad password')
            this.connectionError = this.$t('dash.bots.bad_password')
            this.notify({
              time: 8000,
              title: this.$t('onboarding.connect_natives.error_connection_title'),
              text: this.$t('dash.bots.bad_password'),
              color: 'danger'
            })

          } else if (response.body.invalidUser) {
            console.log('[StepConnectNatives] Invalid user')
            this.connectionError = this.$t('dash.bots.invalid_user')
            this.notify({
              time: 8000,
              title: this.$t('onboarding.connect_natives.error_connection_title'),
              text: this.$t('dash.bots.invalid_user'),
              color: 'danger'
            })

          } else if (response.body.csrfToken) {
            console.log('[StepConnectNatives] CSRF token error')
            this.connectionError = this.$t('dash.bots.csrf_token')
            this.notify({
              time: 8000,
              title: this.$t('onboarding.connect_natives.error_connection_title'),
              text: this.$t('dash.bots.csrf_token'),
              color: 'danger'
            })

          } else if (response.body.notImplementedChall) {
            console.log('[StepConnectNatives] Not implemented challenge')
            this.connectionError = this.$t('dash.bots.not_implemented')
            this.notify({
              time: 10000,
              title: this.$t('onboarding.connect_natives.error_connection_title'),
              text: this.$t('dash.bots.not_implemented'),
              color: 'danger'
            })

          } else {
            // Unknown error
            console.log('[StepConnectNatives] Unknown error in response:', response.body)
            this.connectionError = this.$t('onboarding.connect_natives.error_unknown')
            
            this.notify({
              time: 5000,
              title: this.$t('onboarding.connect_natives.error_connection_title'),
              text: this.$t('onboarding.connect_natives.error_unknown'),
              color: 'danger'
            })
          }
        } else {
          // Request error - but check if it's a 2FA flow error (500 with challengePath)
          console.log('[StepConnectNatives] Request failed:', response)
          console.log('[StepConnectNatives] Response body:', response.body)
          console.log('[StepConnectNatives] Response bodyText:', response.bodyText)
          
          // Check if error message indicates 2FA/challenge flow
          const errorMessage = response.body?.message || response.bodyText || ''
          const isChallengePath = errorMessage.includes('challengePath') || errorMessage.includes('challenge_required')
          const is2FAError = errorMessage.includes('two_factor_required') || errorMessage.includes('twoAuth')
          
          // Also check if body explicitly contains 2FA flags (even with 500 error)
          const bodyHas2FA = response.body && (response.body.twoAuth || response.body.challengeInstagram || response.body.awaitingApproval)
          
          if (isChallengePath || is2FAError || bodyHas2FA) {
            // This is actually a 2FA flow, not a credential error
            console.log('[StepConnectNatives] 500 error is actually 2FA flow - opening modal')
            console.log('[StepConnectNatives] Error message:', errorMessage)
            console.log('[StepConnectNatives] Body has 2FA flag:', bodyHas2FA)
            
            // Refresh pages to get story_config with challenge/2FA info
            await this.refreshPages()
            
            console.log('[StepConnectNatives] Pages refreshed, allPages length:', this.allPages.length)
            console.log('[StepConnectNatives] Refreshed current page:', this.refreshedCurrentPage)
            console.log('[StepConnectNatives] story_config:', this.refreshedCurrentPage?.story_config)
            
            // Check if story_config was properly saved
            if (!this.refreshedCurrentPage || !this.refreshedCurrentPage.story_config) {
              console.warn('[StepConnectNatives] WARNING: story_config is empty after refresh - backend may not have saved 2FA info')
              console.warn('[StepConnectNatives] This may cause the 2FA modal to show an error state')
              // Still try to open the modal - it will show an error message if data is missing
            }
            
            // Open 2FA modal
            this.twoFactorActive = true
            console.log('[StepConnectNatives] twoFactorActive set to true for error 500 with 2FA')
          } else if (response.bodyText && response.bodyText.includes && response.bodyText.includes('560')) {
            // Error 560: specific LinkedIn/Instagram challenge error code
            console.log('[StepConnectNatives] Error 560 detected')
            this.connectionError = this.$t('dash.bots.2fa_560')
            this.notify({
              time: 5000,
              title: this.$t('onboarding.connect_natives.error_connection_title'),
              text: this.$t('dash.bots.2fa_560'),
              color: 'danger'
            })
          } else {
            // Genuine credential/connection error
            this.connectionError = this.$t('onboarding.connect_natives.error_credentials')
            
            this.notify({
              time: 5000,
              title: this.$t('onboarding.connect_natives.error_connection_title'),
              text: this.$t('onboarding.connect_natives.error_credentials'),
              color: 'danger'
            })
          }
        }
      } catch (error) {
        console.error('[StepConnectNatives] Network/Server error:', error)
        
        this.connectionError = this.$t('onboarding.connect_natives.error_network')
        
        this.notify({
          time: 5000,
          title: this.$t('onboarding.connect_natives.error_connection_title'),
          text: this.$t('onboarding.connect_natives.error_network'),
          color: 'danger'
        })
      } finally {
        console.log('[StepConnectNatives] Connection attempt finished')
        this.isConnecting = false
      }
    },

    showSkipWarning() {
      console.log('[StepConnectNatives] Opening skip warning modal')

      const featuresList = this.networkFeatures
        .map(f => `<li>${this.$t(`onboarding.features.${f}.title`)}</li>`)
        .join('')

      this.skipModalContent = `
        <p style="margin-bottom: 16px;">${this.$t('onboarding.modale.native.skip.description', { pageName: this.currentPageName })}</p>
        <ul style="background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 8px; padding: 16px 20px; margin: 16px 0; list-style: none;">
          ${featuresList}
        </ul>
        <p style="font-size: 13px; color: #6b7280; font-style: italic; margin-top: 16px;">${this.$t('onboarding.modale.native.skip.note')}</p>
      `
      this.showSkipModal = true
    },

    closeSkipModal() {
      this.showSkipModal = false
    },

    confirmSkip() {
      console.log('[StepConnectNatives] Skip confirmed by user')
      console.log('[StepConnectNatives] Current page:', this.currentPageName)
      console.log('[StepConnectNatives] Current network:', this.currentNetwork)
      console.log('[StepConnectNatives] Current network index:', this.currentNetworkIndex)
      
      // CRITICAL: Persist onboarding state before navigating
      this.persistOnboardingStateBeforeNavigation()
      
      // Check if there are more networks
      if (this.currentNetworkIndex < this.selectedNetworks.length - 1) {
        const nextNetworkIndex = this.currentNetworkIndex + 1
        const nextNetwork = this.selectedNetworks[nextNetworkIndex]
        console.log('[StepConnectNatives] Navigating to next network:', nextNetwork, 'at index:', nextNetworkIndex)
        
        // Navigate to StepConnectPages with explicit network index in params
        this.$router.push({ 
          name: 'onboarding-connectPages',
          params: { networkIndex: nextNetworkIndex }
        })
      } else {
        // All networks completed, go to summary
        console.log('[StepConnectNatives] All networks completed - redirecting to summary')
        this.$router.push({ name: 'onboarding-summary' })
      }
    },

    skipNativeConnection() {
      // Kept for backward compatibility but now opens warning
      this.showSkipWarning()
    },

    goToNextPage() {
      // Kept for backward compatibility - delegates to continueAfterSuccess
      this.continueAfterSuccess()
    },

    continueAfterSuccess() {
      console.log('[StepConnectNatives] Continue after success - remaining selectedPages:', this.selectedPages.length)
      this.connectionSuccess = false
      this.connectionError = null
      this.nativeCredentials.password = ''
      // Username will be auto-filled by the watcher for the new currentPage

      if (!this.currentPage) {
        console.log('[StepConnectNatives] No more pages to connect - going to next network')
        this.goToNextNetwork()
      }
      // else: currentPage already points to the next unconnected page (connected page removed by filter)
    },

    confirmSkipCurrentPage() {
      console.log('[StepConnectNatives] Skipping native connection for current page:', this.currentPageName)
      this.showSkipModal = false
      this.connectionSuccess = false
      this.connectionError = null
      this.nativeCredentials = { username: '', password: '' }

      // Increment index to skip past current page (it stays in selectedPages since it was not connected)
      this.currentPageIndex++
      console.log('[StepConnectNatives] Advanced currentPageIndex to:', this.currentPageIndex)

      if (!this.currentPage) {
        console.log('[StepConnectNatives] No more pages after skip - going to next network')
        this.goToNextNetwork()
      }
    },

    goToNextNetwork() {
      console.log('[StepConnectNatives] All pages connected for current network')
      console.log('[StepConnectNatives] Current network index:', this.currentNetworkIndex)
      console.log('[StepConnectNatives] Total networks:', this.selectedNetworks.length)
      
      // CRITICAL: Persist onboarding state before navigating
      // This ensures data is not lost when returning to StepConnectPages
      this.persistOnboardingStateBeforeNavigation()
      
      // Check if there are more networks to connect
      if (this.currentNetworkIndex < this.selectedNetworks.length - 1) {
        const nextNetworkIndex = this.currentNetworkIndex + 1
        const nextNetwork = this.selectedNetworks[nextNetworkIndex]
        console.log('[StepConnectNatives] More networks to connect, returning to StepConnectPages')
        console.log('[StepConnectNatives] Next network:', nextNetwork, 'at index:', nextNetworkIndex)
        
        // Return to StepConnectPages for the next network
        // Pass the next network index so StepConnectPages loads the correct network
        this.$router.push({ 
          name: 'onboarding-connectPages',
          params: { 
            fromNatives: true,
            networkIndex: nextNetworkIndex
          }
        })
      } else {
        // All networks processed - go to summary
        console.log('[StepConnectNatives] All networks completed - redirecting to summary')
        this.$router.push({ name: 'onboarding-summary' })
      }
    },

    handleNetworkSelected(network) {
      const index = this.selectedNetworks.indexOf(network)
      if (index !== -1) {
        this.currentNetworkIndex = index
        this.currentPageIndex = 0
      }
    },

    async refreshPages() {
      console.log('[StepConnectNatives] Refreshing pages from backend...')
      
      try {
        const response = await this.getPages()
        
        if (response.ok && response.body && response.body.pages) {
          this.allPages = response.body.pages
          console.log('[StepConnectNatives] Pages refreshed:', this.allPages.length, 'pages')
          
          // Find current page with updated story_config
          const updatedPage = this.allPages.find(p => p.id === this.currentPage.id)
          if (updatedPage) {
            console.log('[StepConnectNatives] Current page updated with story_config:', updatedPage.story_config)
          }
        }
      } catch (error) {
        console.error('[StepConnectNatives] Error refreshing pages:', error)
      }
    },

    async handleTwoFactorSuccess() {
      console.log('[StepConnectNatives] 2FA success - closing modal')
      
      this.twoFactorActive = false
      this.connectionSuccess = true
      this.connectionError = null
      
      // Save the native connection to store and refresh pages
      console.log('[StepConnectNatives] Saving native connection to store...')
      this.$store.commit('onboarding/SET_NATIVE_CONNECTION', {
        network: this.currentNetwork,
        pageId: this.currentPageId,
        connected: true,
        storyConfig: this.refreshedCurrentPage?.story_config || null
      })
      console.log('[StepConnectNatives] Saved to store - nativeConnections:', this.$store.state.onboarding.nativeConnections)

      // Update the page in store with story_config
      if (this.refreshedCurrentPage?.story_config) {
        this.$store.commit('onboarding/UPDATE_PAGE', {
          network: this.currentNetwork,
          pageId: this.currentPageId,
          updates: { story_config: this.refreshedCurrentPage.story_config }
        })
        console.log('[StepConnectNatives] Updated page story_config in store after 2FA')
      }

      // Refresh pages from backend to get latest state
      console.log('[StepConnectNatives] Refreshing pages from backend...')
      try {
        await this.getPages()
        console.log('[StepConnectNatives] Pages refreshed')
      } catch (error) {
        console.error('[StepConnectNatives] Failed to refresh pages after 2FA:', error)
      }
      
      this.notify({
        time: 5000,
        title: this.$t('onboarding.connect_natives.success_title'),
        text: this.$t('onboarding.connect_natives.success_message'),
        color: 'success'
      })
      
      // CRITICAL: Persist state to localStorage after successful 2FA connection
      // Without this, closing the tab after 2FA success would lose the native connection data
      this.persistOnboardingStateBeforeNavigation()
      
      console.log('[StepConnectNatives] 2FA connection complete')
    },

    handleImageError(id) {
      this.imageLoadErrors = { ...this.imageLoadErrors, [id]: true }
    },

    /**
     * Persist complete onboarding state to localStorage before navigating away
     * CRITICAL: This ensures data is not lost when returning to StepConnectPages or going to Summary
     */
    persistOnboardingStateBeforeNavigation() {
      persistOnboardingPages(this.$store)
      console.log('[StepConnectNatives] Onboarding state persisted to localStorage before navigation')
    }
  },

  mounted() {
    console.log('=== [StepConnectNatives] MOUNTED ===')

    // CRITICAL: Set currentNetworkIndex from route params or localStorage BEFORE guard checks.
    // Without this, guards evaluate with currentNetworkIndex=0 (wrong network).

    // Get network from route params if provided
    if (this.$route.params.network) {
      const network = this.$route.params.network
      console.log('[StepConnectNatives] Route param network:', network)
      const index = this.selectedNetworks.indexOf(network)
      if (index !== -1) {
        this.currentNetworkIndex = index
        console.log('[StepConnectNatives] Set network index to:', index)
      } else {
        console.log('[StepConnectNatives] Route param network not found in selected networks')
      }
    }

    // Restore state from localStorage if available (after refresh during OAuth/2FA flow)
    const savedState = localStorage.getItem('onboarding_native_state')
    if (savedState) {
      try {
        const state = JSON.parse(savedState)
        console.log('[StepConnectNatives] Restoring state from localStorage:', state)
        
        // Restore the current network index and page index
        if (typeof state.currentNetworkIndex === 'number') {
          this.currentNetworkIndex = state.currentNetworkIndex
        }
        if (typeof state.currentPageIndex === 'number') {
          this.currentPageIndex = state.currentPageIndex
        }
        
        console.log('[StepConnectNatives] State restored successfully')
        localStorage.removeItem('onboarding_native_state')
      } catch (error) {
        console.error('[StepConnectNatives] Failed to restore state:', error)
        localStorage.removeItem('onboarding_native_state')
      }
    }

    console.log('[StepConnectNatives] Store state:', {
      selectedNetworks: this.selectedNetworks,
      selectedFeatures: this.selectedFeatures,
      connectedPages: this.connectedPages,
      selectedPageIds: this.selectedPageIds,
      nativeConnections: this.$store.state.onboarding.nativeConnections
    })
    console.log('[StepConnectNatives] Current network:', this.currentNetwork)
    console.log('[StepConnectNatives] Selected pages for network:', this.selectedPages)
    console.log('[StepConnectNatives] Current page:', this.currentPage)
    console.log('[StepConnectNatives] Network features requiring native:', this.networkFeatures)

    // Redirect if no networks or no pages
    if (!this.selectedNetworks || this.selectedNetworks.length === 0) {
      // Check if we're in a plain page reload (parent Onboarding.vue will restore Vuex shortly
      // in its own mounted(), which fires after child components due to Vue 2 lifecycle order)
      const hasSavedNetworks = (() => {
        try {
          const str = localStorage.getItem('onboarding_state')
          return str ? ((JSON.parse(str).selectedNetworks?.length || 0) > 0) : false
        } catch (e) {
          console.error('[StepConnectNatives] Failed to parse onboarding_state:', e)
          return false
        }
      })()

      if (hasSavedNetworks) {
        console.log('[StepConnectNatives] Saved state found - deferring guard to selectedNetworks watcher (reload scenario)')
        this._deferredGuard = true
        return
      }

      console.warn('[StepConnectNatives] No networks selected - redirecting to networks step')
      this.$router.push({ name: 'onboarding-networks' })
      return
    } else if (!this.selectedPages || this.selectedPages.length === 0) {
      console.warn('[StepConnectNatives] No pages selected - redirecting to connectPages step')
      this.$router.push({ name: 'onboarding-connectPages' })
      return
    } else if (!this.networkFeatures || this.networkFeatures.length === 0) {
      // If the current network has no native features, move to the next one
      console.log('[StepConnectNatives] Current network has no native features, skipping')
      this.goToNextNetwork()
      return
    }

    // Check if all selected pages already have native connections
    if (this.selectedPages.length === 0) {
      console.log('[StepConnectNatives] All pages already have native connections or no pages selected')
      console.log('[StepConnectNatives] Skipping to next network or dashboard')
      
      // Use nextTick to ensure Vue has finished initializing
      this.$nextTick(() => {
        this.goToNextNetwork()
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '../variables.scss';

.onboarding-connect-natives {
  display: flex;
  min-height: 100vh;
}

.onboarding-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 64px;
  overflow-y: auto;
  overflow-x: hidden;

  &::after {
    content: '';
    display: block;
    height: 100px;
    flex-shrink: 0;
  }
}

/* Header section */
.onboarding-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 20px;
  margin-bottom: 24px;

  .onboarding-network-icon-wrapper {
    position: relative;
    width: 64px;
    height: 64px;
  }

  .onboarding-network-icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: $bg-ui-selected;
    padding: 12px;
  }

  .onboarding-current-page-avatar {
    position: absolute;
    bottom: -4px;
    right: -4px;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    object-fit: cover;

    &.avatar-placeholder {
      background-color: $primary-color;
      color: $bg-ui-selected;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
      font-size: 14px;
      user-select: none;
    }
  }

  .onboarding-header-content {
    flex: 1;
  }

  .onboarding-header-title {
    font-size: 28px;
    font-weight: 700;
    color: #111827;
    margin: 0 0 8px 0;
    text-align: center;
    line-height: 1.3;

    .native-icon {
      display: inline-block;
      vertical-align: -4px;
      margin-left: -6px;
    }
  }

  .onboarding-header-subtitle {
    max-width: 550px;
    font-size: 16px;
    color: #6b7280;
    margin: 0;
    text-align: center;
    line-height: 1.2;
  
    ::v-deep strong {
      color: #111827;
      font-weight: 600;
    }
  }
}

.onboarding-features-badges {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 32px;
  overflow: visible;
}

.onboarding-feature-badge {
  position: relative;
  padding: 6px 12px;
  background: $bg-ui-selected;
  color: $primary-color;
  font-size: 13px;
  font-weight: 500;
  border-radius: 16px;
  text-align: center;
  white-space: nowrap;
  transition: all 0.1s ease;

  &:hover {
    transform: translateY(-1px);
  }

  &[data-tooltip]:not([data-tooltip=""]) {
    cursor: default;

    &::before {
      content: attr(data-tooltip);
      position: absolute;
      bottom: calc(100% + 8px);
      left: 50%;
      transform: translateX(-50%) translateY(-4px);
      padding: 6px 10px;
      background: #424242;
      color: #fff;
      font-size: 12px;
      font-weight: 500;
      border-radius: 6px;
      white-space: normal;
      max-width: 300px;
      min-width: 120px;
      text-align: center;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.1s ease, transform 0.1s ease;
      z-index: 1000;
    }

    &::after {
      content: '';
      position: absolute;
      bottom: calc(100% - 2px);
      left: 50%;
      transform: translateX(-50%) translateY(-4px);
      border: 6px solid transparent;
      border-top-color: #424242;
      pointer-events: none;
      opacity: 0;
      transition: opacity 0.1s ease, transform 0.1s ease;
      z-index: 1000;
    }

    &:hover::before,
    &:hover::after {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }
}
.onboarding-native-form {
  width: 100%;
  max-width: 400px;
  // margin-bottom: 40px;
  background: #f9f9f9;
  padding: 40px;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
}

.onboarding-message {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 14px;
  font-weight: 500;

  svg {
    flex-shrink: 0;
  }

  &.onboarding-message-success {
    background: #d1fae5;
    color: #065f46;
    border: 1px solid #a7f3d0;
  }

  &.onboarding-message-error {
    background: #fee2e2;
    color: #991b1b;
    border: 1px solid #fecaca;
  }
}

.onboarding-form-group {
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
}

.onboarding-form-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #111827;
  margin-bottom: 8px;
}

.onboarding-form-input {
  width: 100%;
  padding: 14px 16px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-size: 16px;
  color: #111827;
  background-color: #fff;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: $primary-color;
  }

  &::placeholder {
    color: #999;
  }
}

.onboarding-btn-connect {
  width: 100%;
  padding: 12px 24px;
  background-color: $primary-color;
  color: #fff;
  border: none;
  border-radius: 40px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover:not(:disabled) {
    background-color: darken($primary-color, 8%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba($primary-color, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  // When connected, transform to look like primary button
  &.is-connected {
    background: $bg-ui-selected;
    color: $primary-color;

    &:hover:not(:disabled) {
      background: $primary-color;
      color: #ffffff;
      box-shadow: 0 4px 12px rgba($primary-color, 0.2);
    }

    .onboarding-step-indicator {
      font-size: 13px;
      opacity: 0.8;
    }
  }

  .onboarding-btn-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }
}

.onboarding-spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.onboarding-btn-skip-native {
  width: 100%;
  padding: 12px 24px;
  background-color: transparent;
  color: #6b7280;
  border: 1px solid #e0e0e0;
  border-radius: 40px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 12px;

  &:hover:not(:disabled) {
    background-color: #f9fafb;
    color: #111827;
    border-color: #d1d5db;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.onboarding-privacy-mention {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 30px;
  font-size: 13px;
  font-weight: 500;
  // color: #6b7280;
  color: #6b1f4d;
  max-width: 380px;
  margin-left: auto;
  margin-right: auto;
}

/* Footer section */
.onboarding-footer-container {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  bottom: 160px;
  right: 160px;
  // margin-right: 20px;
}

.onboarding-footer {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 40px;

  .onboarding-btn-secondary,
  .onboarding-btn-primary {
    padding: 12px 24px;
    font-size: 15px;
    font-weight: 600;
    border-radius: 20px;
    cursor: pointer;
    transition: all 0.2s ease;
    border: none;
  }

  .onboarding-btn-secondary {
    background: transparent;
    color: #6b7280;

    &:hover {
      color: #111827;
    }
  }

  .onboarding-btn-primary {
    background: $bg-ui-selected;
    color: $primary-color;
    display: flex;
    align-items: center;
    gap: 12px;

    &:hover:not(:disabled) {
      background: $primary-color;
      color: #ffffff;

      .onboarding-page-avatar-small {
        border-color: rgba(255, 255, 255, 0.3);
      }

      .avatar-placeholder {
        background-color: rgba(255, 255, 255, 0.2);
        color: #ffffff;
      }
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .onboarding-step-indicator {
      font-size: 13px;
      opacity: 0.8;
    }
  }
}

.onboarding-pages-avatars {
  display: flex;
  gap: 6px;
}

.onboarding-page-avatar-small {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid transparent;
  transition: all 0.2s;

  &.active {
    border-color: $primary-color;
    transform: scale(1.15);
  }
}

.avatar-placeholder {
  background-color: $bg-ui-selected;
  color: $primary-color;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 14px;
  user-select: none;
  line-height: 1;
  transition: all 0.2s;

  &.active {
    border-color: $primary-color;
    transform: scale(1.15);
  }
}

@media (max-width: 800px) {

  .onboarding-native-form {
    padding: 26px;
  }

  .onboarding-footer-container {
    .onboarding-footer {
      .onboarding-step-indicator {
        display: none;
      }
    }
  }
}

// Special case to not crop the Native icon without the last word of th title
@media (max-width: 886px) {
  .onboarding-content {
    .onboarding-header-title {

      padding: 0 17px;

    }
  }
}
// Special case to not crop the Native icon without the last word of th title
@media (max-width: 691px) {
  .onboarding-content {
    .onboarding-header-title {

      padding: 0 40px;

    }
  }
}

/* Success section (shown after connecting, before clicking Continue) */
.onboarding-native-success {
  width: 100%;
  max-width: 400px;
  text-align: center;
  padding: 48px 32px;
  background: #f9f9f9;
  border: 1px solid #e0e0e0;
  border-radius: 16px;
}

.onboarding-native-success-icon {
  margin-bottom: 16px;
}

.onboarding-native-success-title {
  font-size: 20px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 8px 0;
}

.onboarding-native-success-message {
  font-size: 15px;
  color: #6b7280;
  margin: 0;
}

/* Inline skip warning modal — styled to match global modalPopup.scss */
.onboarding-skip-modal-overlay {
  position: fixed;
  inset: 0;
  height: 100vh;
  width: 100vw;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(18, 24, 38, 0.21);
  z-index: 99;
}

.onboarding-skip-modal {
  padding: 32px 40px;
  border-radius: 5.6px;
  background-color: #fff;
  width: 500px;
  max-width: calc(100vw - 32px);
  display: flex;
  flex-direction: column;
  align-items: center;

  h4 {
    font-size: 21.6px;
    color: #121826;
    font-weight: 600;
    line-height: 50px;
    white-space: nowrap;
  }

  .onboarding-skip-modal-body {
    font-size: 13.6px;
    color: #555E75;
    font-weight: 500;
    text-align: center;
    margin-bottom: 25px;
    line-height: 1.5;
    width: 100%;

    ::v-deep p {
      margin-bottom: 14px;
    }

    ::v-deep ul {
      text-align: left;
      list-style: none;

      li {
        padding: 4px 0;
        padding-left: 28px;
        position: relative;
        color: #374151;
        font-size: 14px;
        line-height: 1.5;

        &::before {
          content: '✓';
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
          color: $primary-color;
          font-weight: bold;
          font-size: 16px;
        }
      }
    }
  }

  .onboarding-skip-modal-buttons {
    display: flex;
    align-items: center;
    gap: 16px;
    width: 100%;
    flex-wrap: wrap;

    .button {
      flex-grow: 1;
    }
  }
}

</style>
